import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Users, 
  TrendingUp, 
  Gift, 
  Check, 
  Clock, 
  MousePointerClick, 
  CreditCard,
  Search,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Send,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MILESTONES = [
  { threshold: 5, reward: "€15 Bol.com" },
  { threshold: 10, reward: "€35 Bol.com" },
  { threshold: 25, reward: "€100 Bol.com" },
  { threshold: 50, reward: "€250" },
  { threshold: 100, reward: "€500 bonus" },
];

const PartnersSection = ({ password }) => {
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [sendingReward, setSendingReward] = useState(null);

  useEffect(() => {
    fetchPartners();
    fetchStats();
  }, [password]);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/affiliates?password=${password}`);
      setPartners(response.data.affiliates || []);
    } catch (error) {
      console.error("Error fetching partners:", error);
      toast.error("Kon partners niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/affiliates/stats?password=${password}`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching partner stats:", error);
    }
  };

  const markRewardSent = async (partnerId, threshold) => {
    setSendingReward(`${partnerId}-${threshold}`);
    try {
      await axios.post(
        `${API_URL}/admin/affiliates/${partnerId}/mark-reward-sent?threshold=${threshold}&password=${password}`
      );
      toast.success("Beloning gemarkeerd als verzonden");
      fetchPartners(); // Refresh data
    } catch (error) {
      console.error("Error marking reward sent:", error);
      toast.error("Kon beloning niet markeren");
    } finally {
      setSendingReward(null);
    }
  };

  const filteredPartners = partners.filter(partner => {
    const search = searchTerm.toLowerCase();
    return (
      partner.first_name?.toLowerCase().includes(search) ||
      partner.last_name?.toLowerCase().includes(search) ||
      partner.email?.toLowerCase().includes(search) ||
      partner.referral_code?.toLowerCase().includes(search)
    );
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const getEarnedRewards = (partner) => {
    const earned = [];
    for (const milestone of MILESTONES) {
      if (partner.paid_referrals >= milestone.threshold) {
        const isSent = partner.rewards_sent?.includes(milestone.threshold);
        earned.push({
          ...milestone,
          sent: isSent
        });
      }
    }
    return earned;
  };

  const getPendingRewards = (partner) => {
    return getEarnedRewards(partner).filter(r => !r.sent);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-zinc-200/60 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.total_affiliates || 0}</p>
                <p className="text-xs text-zinc-500">Totaal partners</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-zinc-200/60 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <MousePointerClick className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.total_clicks || 0}</p>
                <p className="text-xs text-zinc-500">Totaal clicks</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-zinc-200/60 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.total_paid_referrals || 0}</p>
                <p className="text-xs text-zinc-500">Betaalde referrals</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-zinc-200/60 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Gift className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.pending_rewards_count || 0}</p>
                <p className="text-xs text-zinc-500">Te verzenden beloningen</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partners Table */}
      <div className="bg-white border border-zinc-200/60 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-zinc-200/60 flex items-center justify-between">
          <h3 className="font-outfit font-medium text-zinc-900">Partners Overzicht</h3>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Zoeken..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-lg border-zinc-200"
            />
          </div>
        </div>

        {filteredPartners.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">
              {searchTerm ? "Geen partners gevonden" : "Nog geen partners aangemeld"}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-zinc-600 font-medium">Partner</TableHead>
                <TableHead className="text-zinc-600 font-medium">Referral Code</TableHead>
                <TableHead className="text-zinc-600 font-medium text-center">Clicks</TableHead>
                <TableHead className="text-zinc-600 font-medium text-center">Referrals</TableHead>
                <TableHead className="text-zinc-600 font-medium text-center">Betaald</TableHead>
                <TableHead className="text-zinc-600 font-medium">Beloningen</TableHead>
                <TableHead className="text-zinc-600 font-medium">Datum</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPartners.map((partner) => {
                const pendingRewards = getPendingRewards(partner);
                const earnedRewards = getEarnedRewards(partner);
                const isExpanded = expandedRow === partner.id;

                return (
                  <React.Fragment key={partner.id}>
                    <TableRow 
                      className={`hover:bg-zinc-50 cursor-pointer ${isExpanded ? "bg-blue-50/50" : ""}`}
                      onClick={() => setExpandedRow(isExpanded ? null : partner.id)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-zinc-900">
                            {partner.first_name} {partner.last_name}
                          </p>
                          <p className="text-xs text-zinc-500">{partner.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-zinc-100 px-2 py-1 rounded text-blue-600">
                          {partner.referral_code}
                        </code>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">{partner.total_clicks || 0}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">{partner.total_referrals || 0}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium text-emerald-600">{partner.paid_referrals || 0}</span>
                      </TableCell>
                      <TableCell>
                        {pendingRewards.length > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            <Clock className="w-3 h-3" />
                            {pendingRewards.length} te verzenden
                          </span>
                        ) : earnedRewards.length > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                            <Check className="w-3 h-3" />
                            {earnedRewards.length} verzonden
                          </span>
                        ) : (
                          <span className="text-zinc-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-500 text-sm">
                        {formatDate(partner.created_at)}
                      </TableCell>
                      <TableCell>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-zinc-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-zinc-400" />
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={8} className="bg-zinc-50/50 p-0">
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            className="p-6"
                          >
                            <div className="grid md:grid-cols-2 gap-6">
                              {/* Partner Details */}
                              <div>
                                <h4 className="font-medium text-zinc-900 mb-3">Partner Details</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-zinc-500">Adres:</span>
                                    <span className="text-zinc-900">{partner.address || "-"}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-zinc-500">Postcode:</span>
                                    <span className="text-zinc-900">{partner.postal_code || "-"}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-zinc-500">Woonplaats:</span>
                                    <span className="text-zinc-900">{partner.city || "-"}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-zinc-500">Land:</span>
                                    <span className="text-zinc-900">{partner.country || "-"}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-zinc-500">Status:</span>
                                    <span className={`font-medium ${partner.status === "active" ? "text-emerald-600" : "text-zinc-500"}`}>
                                      {partner.status === "active" ? "Actief" : partner.status}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Rewards Management */}
                              <div>
                                <h4 className="font-medium text-zinc-900 mb-3">Beloningen</h4>
                                <div className="space-y-2">
                                  {MILESTONES.map((milestone) => {
                                    const isEarned = partner.paid_referrals >= milestone.threshold;
                                    const isSent = partner.rewards_sent?.includes(milestone.threshold);
                                    const isSending = sendingReward === `${partner.id}-${milestone.threshold}`;

                                    return (
                                      <div 
                                        key={milestone.threshold}
                                        className={`flex items-center justify-between p-3 rounded-lg ${
                                          isEarned 
                                            ? isSent 
                                              ? "bg-emerald-50 border border-emerald-200" 
                                              : "bg-amber-50 border border-amber-200"
                                            : "bg-zinc-100 border border-zinc-200"
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          <span className={`text-xs font-bold px-2 py-1 rounded ${
                                            isEarned ? "bg-white text-zinc-900" : "bg-zinc-200 text-zinc-500"
                                          }`}>
                                            {milestone.threshold}
                                          </span>
                                          <span className={isEarned ? "text-zinc-900" : "text-zinc-500"}>
                                            {milestone.reward}
                                          </span>
                                        </div>
                                        
                                        {isEarned && !isSent && (
                                          <Button
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              markRewardSent(partner.id, milestone.threshold);
                                            }}
                                            disabled={isSending}
                                            className="bg-amber-500 hover:bg-amber-600 text-white text-xs"
                                          >
                                            {isSending ? (
                                              <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                              <>
                                                <Send className="w-3 h-3 mr-1" />
                                                Verzonden
                                              </>
                                            )}
                                          </Button>
                                        )}
                                        
                                        {isEarned && isSent && (
                                          <span className="text-xs text-emerald-600 flex items-center gap-1">
                                            <Check className="w-3 h-3" />
                                            Verzonden
                                          </span>
                                        )}
                                        
                                        {!isEarned && (
                                          <span className="text-xs text-zinc-400">
                                            Nog {milestone.threshold - partner.paid_referrals} te gaan
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default PartnersSection;
